import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RSVP } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RSVPCard({ variant = "card" }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company_name: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function validate() {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim()) e.last_name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.phone.trim() || !/^[\+\d\s\-\(\)\.]{7,25}$/.test(form.phone)) e.phone = "Valid phone required";
    if (!form.company_name.trim()) e.company_name = "Required";
    return e;
  }

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please complete the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/rsvp`, form);
      setSubmitted(data);
      toast.success("Request received. We\u2019ll be in touch.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : (typeof detail === "string" ? detail : "Could not submit RSVP. Please try again.");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const wrapperStyle =
    variant === "bare"
      ? {}
      : {
          border: "1px solid var(--vein)",
          background: "rgba(255,255,255,0.80)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          padding: "32px",
        };

  return (
    <div id="rsvp" data-testid={RSVP.section} style={wrapperStyle}>
      {!submitted ? (
        <form onSubmit={submit} noValidate>
          <div className="overline">RSVP Now</div>
          <h3
            className="font-serif mt-4"
            style={{
              fontSize: "clamp(30px, 3.6vw, 44px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            Reserve your seat.
          </h3>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div className={`field ${errors.first_name ? "error" : ""}`}>
              <label htmlFor="first_name">First Name</label>
              <input
                id="first_name"
                data-testid={RSVP.firstName}
                value={form.first_name}
                onChange={set("first_name")}
              />
              {errors.first_name && <div className="err-msg">{errors.first_name}</div>}
            </div>
            <div className={`field ${errors.last_name ? "error" : ""}`}>
              <label htmlFor="last_name">Last Name</label>
              <input
                id="last_name"
                data-testid={RSVP.lastName}
                value={form.last_name}
                onChange={set("last_name")}
              />
              {errors.last_name && <div className="err-msg">{errors.last_name}</div>}
            </div>
            <div className={`field md:col-span-2 ${errors.email ? "error" : ""}`}>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                data-testid={RSVP.email}
                value={form.email}
                onChange={set("email")}
              />
              {errors.email && <div className="err-msg">{errors.email}</div>}
            </div>
            <div className={`field ${errors.phone ? "error" : ""}`}>
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                type="tel"
                data-testid={RSVP.phone}
                value={form.phone}
                onChange={set("phone")}
              />
              {errors.phone && <div className="err-msg">{errors.phone}</div>}
            </div>
            <div className={`field ${errors.company_name ? "error" : ""}`}>
              <label htmlFor="company_name">Company Name</label>
              <input
                id="company_name"
                data-testid={RSVP.company}
                value={form.company_name}
                onChange={set("company_name")}
              />
              {errors.company_name && <div className="err-msg">{errors.company_name}</div>}
            </div>
          </div>

          <button
            data-testid={RSVP.submit}
            className="btn btn-ember w-full mt-8"
            style={{ width: "100%" }}
            disabled={submitting}
            type="submit"
          >
            {submitting ? "Submitting\u2026" : "Submit RSVP"}
          </button>

          <div
            className="mt-6 text-[11px]"
            style={{ color: "var(--stone)", letterSpacing: "0.02em" }}
          >
            By submitting, you agree to be contacted by the Cara Capital team regarding this invitation.
          </div>
        </form>
      ) : (
        <SuccessState guest={submitted} />
      )}
    </div>
  );
}

function SuccessState({ guest }) {
  return (
    <div data-testid={RSVP.successState} className="flex flex-col items-start">
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 999,
          border: "1px solid var(--ember)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          style={{ color: "var(--ember)" }}
        >
          <path d="M4 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div className="overline mt-8">Request received</div>
      <h3
        className="font-serif mt-4"
        style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}
      >
        Thank you, <span className="italic-serif grad-text">{guest?.first_name || "friend"}.</span>
      </h3>
      <p
        className="mt-6 text-[15.5px] max-w-md"
        style={{ color: "var(--stone)", lineHeight: 1.65 }}
      >
        The Cara team will be in touch shortly regarding your invitation to{" "}
        <em>Women in Dentistry Investor Social</em> in Chicago on Wednesday, Feb 24, 2027.
      </p>
      <p className="mt-6 text-[13px]" style={{ color: "var(--stone)" }}>
        A confirmation has been logged under {guest?.email}.
      </p>
    </div>
  );
}
